import { AlertTriangle, CheckCircle2, HelpCircle, Pencil } from 'lucide-react';

import type { FieldConfidenceResponse } from '../api';
import type { FieldEditState } from '../hooks/useFieldEdit';
import { useFieldAggregates } from '../hooks/useFieldAggregates';

interface DocumentStatsHudProps {
  fields: ReadonlyArray<FieldConfidenceResponse>;
  edits: Record<string, FieldEditState>;
  globalScore?: number | null | undefined;
}

/**
 * Persistent HUD banner pinned above the reviewer's workspace. Surfaces the document's
 * overall health (global confidence + per-bucket counts + manual edits) so the operator
 * always sees the same numbers regardless of how far they have scrolled the field list.
 *
 * Apple-style materials: rounded-2xl card with a frosted-glass background so subtle PDF
 * texture bleeds through when there is content right behind it. Color is reserved for
 * the small dots/numbers; the card itself stays neutral to keep the page calm.
 */
export function DocumentStatsHud({
  fields,
  edits,
  globalScore,
}: DocumentStatsHudProps): JSX.Element {
  const counts = useFieldAggregates(fields, edits);
  const globalPct =
    globalScore == null ? null : Math.max(0, Math.min(100, Math.round(globalScore * 100)));

  return (
    <section
      aria-label="Resumen de confianza del documento"
      className="flex items-stretch gap-3 overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/80 px-3 py-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/65 sm:gap-4 sm:px-5 sm:py-4"
      data-testid="document-stats-hud"
    >
      <ConfidenceRing pct={globalPct} />
      <div className="hidden h-auto w-px self-stretch bg-slate-200/70 sm:block" aria-hidden />
      <div className="flex min-w-0 flex-1 items-stretch gap-4">
        <Segment Icon={CheckCircle2} label="Alto" value={counts.ok} total={counts.total} tone="emerald" />
        <Segment Icon={HelpCircle} label="Medio" value={counts.warning} total={counts.total} tone="amber" />
        <Segment Icon={AlertTriangle} label="Bajo" value={counts.critical} total={counts.total} tone="rose" />
        <Segment Icon={Pencil} label="Editados" value={counts.edited} total={counts.total} tone="indigo" />
      </div>
    </section>
  );
}

interface ConfidenceRingProps {
  pct: number | null;
}

/**
 * Radial gauge for the global confidence. Stroked SVG so the colour transitions stay
 * crisp without a runtime canvas. The hue follows the same tonal scale as the per-field
 * meter (emerald/amber/rose) so the operator gets the same visual signal across the page.
 */
function ConfidenceRing({ pct }: ConfidenceRingProps): JSX.Element {
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const value = pct ?? 0;
  const dash = (value / 100) * circumference;
  const tone = pct == null ? 'slate' : pct >= 80 ? 'emerald' : pct >= 60 ? 'amber' : 'rose';
  const stroke = {
    emerald: 'stroke-emerald-500',
    amber: 'stroke-amber-500',
    rose: 'stroke-rose-500',
    slate: 'stroke-slate-300',
  }[tone];
  const text = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-800',
    rose: 'text-rose-700',
    slate: 'text-slate-500',
  }[tone];

  return (
    <div className="flex flex-none items-center gap-3 pr-2">
      <div className="relative h-14 w-14">
        <svg viewBox="0 0 56 56" className="h-14 w-14 -rotate-90">
          <circle cx="28" cy="28" r={radius} className="stroke-slate-200" strokeWidth="6" fill="none" />
          <circle
            cx="28"
            cy="28"
            r={radius}
            className={`${stroke} transition-[stroke-dashoffset] duration-700 ease-out`}
            strokeWidth="6"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={`${dash} ${circumference}`}
          />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums ${text}`}>
          {pct == null ? '—' : `${pct}%`}
        </div>
      </div>
      <div className="hidden flex-col leading-tight sm:flex">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Confianza global
        </span>
        <span className="text-xs text-slate-600">media de {pct == null ? '0' : '100'}</span>
      </div>
    </div>
  );
}

type Tone = 'emerald' | 'amber' | 'rose' | 'indigo';

const TONE: Record<Tone, { dot: string; text: string; soft: string }> = {
  emerald: { dot: 'bg-emerald-500', text: 'text-emerald-700', soft: 'text-emerald-600/70' },
  amber: { dot: 'bg-amber-500', text: 'text-amber-800', soft: 'text-amber-700/70' },
  rose: { dot: 'bg-rose-500', text: 'text-rose-700', soft: 'text-rose-600/70' },
  indigo: { dot: 'bg-indigo-500', text: 'text-indigo-700', soft: 'text-indigo-600/70' },
};

interface SegmentProps {
  Icon: typeof CheckCircle2;
  label: string;
  value: number;
  total: number;
  tone: Tone;
}

function Segment({ Icon, label, value, total, tone }: SegmentProps): JSX.Element {
  const cls = TONE[tone];
  const pct = total === 0 ? 0 : Math.round((value / total) * 100);
  return (
    <div className="flex min-w-[112px] flex-1 flex-col justify-center gap-1 px-1">
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${cls.dot}`} aria-hidden />
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${cls.text}`}>
          <Icon className="h-3 w-3" aria-hidden strokeWidth={2.25} />
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-semibold tabular-nums text-slate-900">{value}</span>
        <span className={`text-[11px] tabular-nums ${cls.soft}`}>· {pct}%</span>
      </div>
    </div>
  );
}
